import { useCallback, useEffect, useState } from 'react';
import ApiDatabase from 'server';
import ActivityEntry, { fullNameOf } from 'components/activity/ActivityEntry';
import { labelForField } from 'helper/companyFieldLabels';
import { DocumentIcon } from 'assets/icons';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import { formatCompanyActivityValue } from 'helper/companyActivity';
import UseContext from 'hooks/useContext';
import useModal from 'components/modal/useModal';
import { companyRoleLabel } from 'constants/companyRoles';

/**
 * The company sheet's history (JBWEB-1211).
 *
 * Only what the panel itself can change reaches this list: missions, contracts
 * and HR keep their own logs in their own collections and never enter it.
 */
const EVENT_LABELS = {
  creation_entreprise: "a créé l'entreprise",
  modification_infos_entreprise: "a modifié les informations de l'entreprise",
  modification_brief_entreprise: "a mis à jour le brief de l'entreprise",
  ajout_grand_groupe: "a associé l'entreprise au groupe",
  suppression_grand_groupe: "a retiré l'entreprise du groupe",
  affichage_estimation_cout: "a modifié l'affichage de l'estimation de coût",
  ajouter_membre: 'a ajouté le membre',
  modifier_membre: 'a modifié le membre',
  supprimer_membre: 'a supprimé le membre',
  renvoyer_invitation_membre: "a renvoyé l'invitation au membre",
  ajout_coefficient_facturation: 'a défini les coefficients de facturation',
  modification_coefficient_facturation:
    'a mis à jour les coefficients de facturation',
  application_coefficients_defaut_secteur:
    'a appliqué la grille de coefficients par défaut du secteur',
  ajout_heure_supplementaire: 'a défini les majorations',
  modification_heure_supplementaire: 'a mis à jour les majorations',
  ajout_heure_nuit: 'a défini les majorations',
  modification_heure_nuit: 'a mis à jour les majorations',
  ajout_heure_dimanche: 'a défini les majorations',
  modification_heure_dimanche: 'a mis à jour les majorations',
  ajout_heure_jour_ferie_travailles: 'a défini les majorations',
  modification_heure_jour_ferie_travailles: 'a mis à jour les majorations',
  ajout_installation_collective: 'a défini les installations collectives',
  modification_installation_collective:
    'a mis à jour les installations collectives',
  ajout_prime_indemnite: 'a ajouté une prime',
  modification_prime_indemnite: 'a modifié la prime',
  suppression_prime_indemnite: 'a supprimé une prime',
  ajout_prime_autre: 'a ajouté une prime « autre »',
  modification_prime_autre: 'a modifié une prime « autre »',
  ajout_ticket_restaurant: 'a défini le ticket restaurant',
  modification_ticket_restaurant: 'a mis à jour le ticket restaurant',
  modification_secteur_pennylane: 'a modifié le secteur Pennylane',
  modifier_informations_facturation: 'a modifié les informations de facturation',
  ajout_post_it: 'a ajouté un post-it',
  modification_post_it: 'a modifié le post-it',
  suppression_post_it: 'a supprimé le post-it',
  entreprise_ajout_collaborateur: 'a ajouté un collaborateur',
  entreprise_modification_collaborateur: 'a modifié un collaborateur',
  entreprise_suppression_collaborateur: 'a supprimé un collaborateur',
  entreprise_transfert_admin: "a transféré le rôle d'administrateur",
  entreprise_modification_telephone: 'a modifié le téléphone',
  entreprise_modification_permissions: 'a modifié les permissions',
  creation_perimetre: 'a créé le périmètre',
  modification_perimetre: 'a modifié le périmètre',
  archivage_perimetre: 'a archivé le périmètre',
  suppression_perimetre: 'a supprimé le périmètre',
  restauration_perimetre: 'a restauré le périmètre',
  modification_perimetres_membre: 'a modifié les périmètres de',
  modifier_notifications_membre: 'a modifié les notifications de',
};

const PERIMETER_EVENTS = new Set([
  'creation_perimetre',
  'modification_perimetre',
  'archivage_perimetre',
  'suppression_perimetre',
  'restauration_perimetre',
  'modification_perimetres_membre',
  'modifier_notifications_membre',
]);

// The subjects and levels of a member's notifications, as the contact screen
// names them.
const NOTIFICATION_TYPE_LABELS = {
  invoices: 'Facture',
  reminders: 'Relances factures',
  contracts: 'Contrats',
  timesheets: 'Relevés d’heures',
  missions: 'Missions',
};
const NOTIFICATION_LEVEL_LABELS = {
  none: 'Aucune',
  perimeter: 'Périmètre',
  all: 'Toutes',
};
const notificationLevelLabel = (level) =>
  NOTIFICATION_LEVEL_LABELS[level] ?? '—';

const plural = (count, singular, pluralForm) =>
  `${count} ${count > 1 ? pluralForm : singular}`;

// Members arrive as { id, firstname, lastname }; the per-member event stores
// plain perimeter names (or { name }). Both reduce to a display string.
const entryName = (entry) => {
  if (entry && typeof entry === 'object') {
    return entry.name ?? fullNameOf(entry) ?? '';
  }
  return entry === null || entry === undefined ? '' : String(entry);
};
const entryKey = (entry) =>
  entry && typeof entry === 'object' && entry.id ? entry.id : entryName(entry);

const asList = (value) => (Array.isArray(value) ? value : []);

/** What was added to / removed from a list, compared by id (else by name). */
const listDelta = (before, after) => {
  const beforeKeys = new Set(asList(before).map(entryKey));
  const afterKeys = new Set(asList(after).map(entryKey));
  return {
    added: asList(after)
      .filter((entry) => !beforeKeys.has(entryKey(entry)))
      .map(entryName)
      .filter(Boolean),
    removed: asList(before)
      .filter((entry) => !afterKeys.has(entryKey(entry)))
      .map(entryName)
      .filter(Boolean),
  };
};

const changeOf = (modifications, field) =>
  modifications.find((change) => change.field === field);

/**
 * The lines under a perimeter event, read from what the api records
 * (useCases/perimeter/*). A diff of raw fields ("status", "members") would
 * read as noise here, so these lines replace it.
 */
export const perimeterDetailLines = (type, details, modifications) => {
  const members = changeOf(modifications, 'members');
  const lines = [];

  if (type === 'creation_perimetre') {
    const names = listDelta([], members?.after).added;
    if (names.length) lines.push(`Membres : ${names.join(', ')}`);
  } else if (type === 'modification_perimetre') {
    const name = changeOf(modifications, 'name');
    if (name) {
      lines.push(
        `Renommé : ${entryName(name.before)} → ${entryName(name.after)}`,
      );
    }
    const { added, removed } = listDelta(members?.before, members?.after);
    if (added.length) lines.push(`Membres ajoutés : ${added.join(', ')}`);
    if (removed.length) lines.push(`Membres retirés : ${removed.join(', ')}`);
  } else if (type === 'archivage_perimetre') {
    // Without a destination the api counts no transferred member: the members
    // it detached are only in the "members" change.
    const memberCount = details?.transferTo?.name
      ? Number(details?.transferredMembers) || 0
      : listDelta(members?.before, members?.after).removed.length;
    const counts = `${plural(
      Number(details?.transferredMissions) || 0,
      'mission',
      'missions',
    )}, ${plural(memberCount, 'membre', 'membres')}`;
    lines.push(
      details?.transferTo?.name
        ? `Transfert vers le périmètre ${details.transferTo.name} : ${counts}`
        : `Sans périmètre de destination : ${counts}`,
    );
  } else if (type === 'modification_perimetres_membre') {
    // Not written by the api yet: before / after are lists of perimeter names.
    const change = changeOf(modifications, 'perimeters') ?? modifications[0];
    const { added, removed } = listDelta(change?.before, change?.after);
    if (added.length) lines.push(`Périmètres ajoutés : ${added.join(', ')}`);
    if (removed.length) lines.push(`Périmètres retirés : ${removed.join(', ')}`);
  } else if (type === 'modifier_notifications_membre') {
    // One change per subject that moved, levels as the api stores them.
    modifications.forEach((change) => {
      const label = NOTIFICATION_TYPE_LABELS[change.field] ?? change.field;
      lines.push(
        `${label} : ${notificationLevelLabel(change.before)} → ${notificationLevelLabel(change.after)}`,
      );
    });
  }
  return lines;
};

// A billing contact line (JBWEB-1285) names the contact and, for an edit, one
// line per field that moved; its label and texts are localised.
const BILLING_CONTACT_EVENTS = new Set([
  'ajouter_contact_facturation',
  'modifier_contact_facturation',
  'supprimer_contact_facturation',
]);
const BILLING_CONTACT_BOOLEAN_FIELDS = ['invoices', 'reminders'];

export const billingContactDetailLines = (type, modifications, language) => {
  if (type !== 'modifier_contact_facturation') return [];
  const text = (key) =>
    language.getValue(`companyBillingContacts.activity.${key}`, '');
  const show = (field, value) => {
    if (BILLING_CONTACT_BOOLEAN_FIELDS.includes(field)) {
      if (value === true) return text('yes');
      if (value === false) return text('no');
    }
    return value === null || value === undefined || value === ''
      ? text('empty')
      : String(value);
  };
  // Functions as replacements: a "$&" in an address would otherwise be read
  // as a pattern.
  return modifications.map((change) =>
    text('line')
      .replace('{field}', () => text(`fields.${change.field}`) || change.field)
      .replace('{before}', () => show(change.field, change.before))
      .replace('{after}', () => show(change.field, change.after)),
  );
};

// A member line is about one person: it names them, and says what followed
// the change (HubSpot, the invitation), which the api records beside the diff.
const MEMBER_EVENTS = new Set([
  'ajouter_membre',
  'modifier_membre',
  'supprimer_membre',
  'renvoyer_invitation_membre',
]);
// "skipped" is left out: nothing was tried, there is nothing to report.
const HUBSPOT_SYNC_TEXT_KEYS = {
  updated: 'updated',
  not_found: 'notFound',
  failed: 'failed',
};

// Member lines store the role as its number, and a removal the whole member.
const withRoleName = (language, field, value) => {
  const nameOf = (role) =>
    role === null || role === undefined
      ? role
      : companyRoleLabel(language, role) || role;
  if (field === 'role') return nameOf(value);
  if (field === 'membre' && value?.role !== undefined) {
    return { ...value, role: nameOf(value.role) };
  }
  return value;
};

/** Sizes come from S3 in bytes; the card shows what the design shows. */
const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes)) return null;
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

/**
 * One side of a change.
 *
 * Values reach here as whatever the field holds — a number, a sub-document, a
 * list of night-hour ranges — so the formatter returns lines and the panel
 * bullets them only when there is more than one.
 */
const ChangeSide = ({ title, value, minimumWage }) => {
  const lines = formatCompanyActivityValue(value, minimumWage);

  return (
    <div className={'flex-1 min-w-[160px] p-3'}>
      <div className={'text-gray-500 ft-sm mb-1'}>{title}</div>
      {lines.length === 0 ? (
        <div className={'text-gray-400'}>—</div>
      ) : lines.length === 1 ? (
        <div className={'text-gray-900 break-words'}>{lines[0]}</div>
      ) : (
        <ul className={'list-disc list-inside text-gray-900'}>
          {lines.map((line, index) => (
            <li key={index} className={'break-words'}>
              {line}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const SlideOverCompanyActivity = ({ companyInfo }) => {
  const { toggle } = useModal();
  const { setModalData, language } = UseContext();
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const token = localStorage.getItem('xsrfToken');

  const fetchPage = useCallback(
    (wanted) => {
      if (!companyInfo?._id) return;
      setIsLoading(true);
      ApiDatabase.getCompanyActivityLogs(
        { token, idCompany: companyInfo._id, page: wanted, limit: 30 },
        (data) => {
          // Appended, not replaced: "Voir plus" extends the list rather than
          // paging it, which is how the mission timeline behaves.
          setActivities((previous) =>
            wanted === 1 ? data.activities : [...previous, ...data.activities]
          );
          setTotalPages(data.totalPages ?? 1);
          setPage(wanted);
          setIsLoading(false);
        },
        (err) => {
          if (err.name === 'CanceledError') return;
          console.error(err);
          setError("L'activité n'a pas pu être chargée.");
          setIsLoading(false);
        }
      );
    },
    [companyInfo?._id, token]
  );

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  if (error) {
    return <div className={'text-red-500'}>{error}</div>;
  }

  if (isLoading && activities.length === 0) {
    return (
      <div className={'flex flex-col gap-4 animate-pulse'}>
        {[0, 1, 2].map((row) => (
          <div key={row} className={'flex gap-3'}>
            <div className={'w-8 h-8 rounded-full bg-gray-200 shrink-0'} />
            <div className={'flex flex-col gap-2 flex-1'}>
              <div className={'h-4 w-1/3 bg-gray-200 rounded'} />
              <div className={'h-4 w-2/3 bg-gray-200 rounded'} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <CompactEmptyState
        glyph="clock"
        compact
        title={language.getValue(
          'companyContacts.activity.emptyTitle',
          'Aucune modification enregistrée',
        )}
        description={language.getValue(
          'companyContacts.activity.emptyText',
          'Les modifications apportées à cette entreprise apparaîtront ici.',
        )}
      />
    );
  }

  return (
    <div className={'flex flex-col'}>
      {activities.map((activity, index) => {
        const author = activity.admin;
        const isBillingContactEvent = BILLING_CONTACT_EVENTS.has(
          activity.type,
        );
        const label = isBillingContactEvent
          ? language.getValue(
              `companyBillingContacts.activity.${activity.type}`,
              '',
            ) || activity.type
          : (EVENT_LABELS[activity.type] ?? activity.type);
        // Entries written before JBWEB-1211 also carry a `modifications`
        // key, holding an object rather than a list.
        const modifications = Array.isArray(activity.details?.modifications)
          ? activity.details.modifications
          : [];
        const justificatif = activity.justificatif;
        // At most three, then a count: an entry naming twelve fields is a
        // paragraph, and the diff below says the rest.
        const changedLabels = modifications
          .map((change) => labelForField(change.field))
          .filter(Boolean);
        const changedFields =
          changedLabels.length === 0
            ? null
            : changedLabels.length <= 3
              ? changedLabels.join(', ')
              : `${changedLabels.slice(0, 3).join(', ')} +${changedLabels.length - 3}`;
        const isPerimeterEvent = PERIMETER_EVENTS.has(activity.type);
        // These events name their subject and replace the raw diff by lines.
        const isLineEvent = isPerimeterEvent || isBillingContactEvent;
        const detailLines = isPerimeterEvent
          ? perimeterDetailLines(activity.type, activity.details, modifications)
          : isBillingContactEvent
            ? billingContactDetailLines(activity.type, modifications, language)
            : [];
        const isMemberEvent = MEMBER_EVENTS.has(activity.type);
        const displayValue = (field, value) =>
          isMemberEvent ? withRoleName(language, field, value) : value;
        const hubspotSyncKey = isMemberEvent
          ? HUBSPOT_SYNC_TEXT_KEYS[activity.details?.hubspotSync]
          : undefined;
        const invitationResent = isMemberEvent
          ? activity.details?.invitationResent
          : undefined;
        // The api writes `invitationSent` (as false) only when the mail did
        // not leave: a line without it, an older one included, says nothing.
        const isAddition = activity.type === 'ajouter_membre';
        const fromHubspot =
          isAddition && activity.details?.origin === 'hubspot';
        const additionMailFailed =
          isAddition && activity.details?.invitationSent === false;

        return (
          <div key={activity._id ?? index} className={'flex flex-col'}>
            <ActivityEntry
              person={author}
              displayName={fullNameOf(author) || 'Utilisateur supprimé'}
              date={activity.date}
              // The client acting on their own sheet is the fact a history is
              // often kept for, so their entry wears the company mark rather
              // than passing for one of ours.
              fallbackIcon={
                activity.actorType === 'company' && !author ? 'company' : undefined
              }
            >
              <div className={'flex flex-col gap-2'}>
                <div className={'text-sm text-gray-600'}>
                  {label}
                  {/* What actually moved, named. The stored subject is often
                      the section rather than the field — "Informations
                      légales" after "a modifié les informations de
                      l'entreprise" repeats the verb and leaves the reader
                      hunting for the SIRET in a grey caption. A member line
                      is the exception: its subject is the person. */}
                  {isLineEvent ? (
                    activity.details?.subject && (
                      <span className={'text-gray-900 font-semi-bold'}>
                        {' '}
                        {activity.details.subject}
                      </span>
                    )
                  ) : isMemberEvent ? (
                    <>
                      {activity.details?.subject && (
                        <span className={'text-gray-900 font-semi-bold'}>
                          {' '}
                          {activity.details.subject}
                        </span>
                      )}
                      {changedFields && <span>{` · ${changedFields}`}</span>}
                    </>
                  ) : changedFields ? (
                    <span className={'text-gray-900 font-semi-bold'}>
                      {' '}
                      {changedFields}
                    </span>
                  ) : (
                    activity.details?.subject && (
                      <span className={'text-gray-900 font-semi-bold'}>
                        {' '}
                        {activity.details.subject}
                      </span>
                    )
                  )}
                  {activity.actorType === 'company' && (
                    <span className={'text-xs text-gray-400'}> · entreprise</span>
                  )}
                </div>

                {/* Entries written before JBWEB-1211 carry no "before": the
                    value was never captured and is not invented here, so they
                    stand as a dated action with no diff. */}
                {detailLines.map((line) => (
                  <div key={line} className={'text-xs text-gray-500'}>
                    {line}
                  </div>
                ))}

                {!isLineEvent && modifications.length > 0 && (
                  <div className={'border rounded-lg divide-y'}>
                    {modifications.map((change, changeIndex) => (
                      <div key={changeIndex} className={'flex flex-col'}>
                        {/* Which setting moved, named as the panel names it —
                            without it the two columns float free of what they
                            describe. */}
                        <div
                          className={
                            'px-3 pt-2 text-xs font-medium text-gray-700'
                          }
                        >
                          {labelForField(change.field)}
                        </div>
                        <div className={'flex flex-wrap'}>
                          <ChangeSide
                            title={'Avant'}
                            value={displayValue(change.field, change.before)}
                            minimumWage={activity.details?.minimumWage}
                          />
                          <div className={'w-px bg-gray-200 self-stretch'} />
                          <ChangeSide
                            title={'Après'}
                            value={displayValue(change.field, change.after)}
                            minimumWage={activity.details?.minimumWage}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {hubspotSyncKey && (
                  <div className={'text-xs text-gray-400'}>
                    {language.getValue(
                      `companyContacts.activity.hubspot.${hubspotSyncKey}`,
                      '',
                    )}
                  </div>
                )}
                {typeof invitationResent === 'boolean' && (
                  <div className={'text-xs text-gray-400'}>
                    {language.getValue(
                      `companyContacts.activity.invitation.${
                        invitationResent ? 'resent' : 'failed'
                      }`,
                      '',
                    )}
                  </div>
                )}
                {fromHubspot && (
                  <div className={'text-xs text-gray-400'}>
                    {language.getValue(
                      'companyContacts.activity.invitation.fromHubspot',
                      '',
                    )}
                  </div>
                )}
                {additionMailFailed && (
                  <div className={'text-xs text-gray-400'}>
                    {language.getValue(
                      'companyContacts.activity.invitation.notSent',
                      '',
                    )}
                  </div>
                )}
                {/* BeSTT prints the invoice: a billing address it did not take
                    is worth seeing in the history (JBWEB-1268). */}
                {activity.type === 'modifier_informations_facturation' &&
                  activity.details?.besttSync === 'failed' && (
                    <div className={'text-xs text-gray-400'}>
                      {language.getValue(
                        'companyBilling.besttNotUpdated',
                        'BeSTT n’a pas été mis à jour'
                      )}
                    </div>
                  )}

                {justificatif && (
                  <button
                    type={'button'}
                    className={
                      'border rounded-lg p-3 flex items-center gap-2 text-left hover:bg-gray-50 w-fit'
                    }
                    onClick={() => {
                      // The entry's id, not a link: the URL is signed when the
                      // modal opens and expires shortly after.
                      // The size travels so the modal can name the document
                      // while the signed link is still being fetched.
                      setModalData({
                        idLog: activity._id,
                        size: justificatif.size,
                      });
                      toggle('company_activity_justificatif');
                    }}
                  >
                    <DocumentIcon wh={20} color={'#111827'} />
                    <span className={'text-gray-900'}>
                      Justificatif de modification client
                    </span>
                    {formatFileSize(justificatif.size) && (
                      <span className={'text-gray-400 ft-sm'}>
                        · {formatFileSize(justificatif.size)}
                      </span>
                    )}
                  </button>
                )}
              </div>
            </ActivityEntry>
          </div>
        );
      })}

      {page < totalPages && (
        <button
          type={'button'}
          className={'text-gray-500 underline py-3'}
          disabled={isLoading}
          onClick={() => fetchPage(page + 1)}
        >
          {isLoading ? 'Chargement…' : 'Voir plus'}
        </button>
      )}
    </div>
  );
};

export default SlideOverCompanyActivity;
